/** Stats: totals, the year bars, the month heatmap, top lists, events and a year in review. */
import { getJson } from "./request";
import type { YearCount } from "./types";

export interface Ranked {
  id: number;
  name: string;
  count: number;
  location?: string | null;
}
export interface StatsDto {
  totals: { shows: number; bands: number; venues: number; events: number; first: string | null; last: string | null; with_flyer: number };
  by_year: YearCount[];
  by_month: { year: number; month: number; count: number }[];
  top_bands: Ranked[];
  top_venues: Ranked[];
  events: Ranked[];
}
export interface YearReview {
  year: number;
  shows: number;
  bands: number;
  venues: number;
  top_bands: Ranked[];
  top_venues: Ranked[];
  first: { id: number; date: string; venue: string };
  last: { id: number; date: string; venue: string };
}

export const fetchStats = () => getJson<StatsDto>("/api/stats", "Could not load stats");
export const fetchYear = (year: number) => getJson<YearReview>(`/api/stats/years/${year}`, "Could not load that year");

/** Every year from first to last, oldest first, with a 0 for a year with no shows. */
export function yearSeries(byYear: YearCount[]): YearCount[] {
  if (!byYear.length) return [];
  const counts = new Map(byYear.map((y) => [y.year, y.count]));
  const years = byYear.map((y) => y.year);
  const lo = Math.min(...years), hi = Math.max(...years);
  return Array.from({ length: hi - lo + 1 }, (_, i) => ({ year: lo + i, count: counts.get(lo + i) ?? 0 }));
}

/** Gridlines every `step`, up to the first one at or above the max. */
export function ticks(max: number, step = 10): number[] {
  const top = Math.max(step, Math.ceil(max / step) * step);
  return Array.from({ length: top / step + 1 }, (_, i) => i * step);
}

export const pct = (part: number, whole: number) => (whole ? Math.round((100 * part) / whole) : 0);

export interface Heatmap {
  years: number[];
  max: number;
  count: (year: number, month: number) => number;
}
/** Newest year first, as the mockup reads top to bottom. */
export function heatmap(byMonth: StatsDto["by_month"]): Heatmap {
  const cells = new Map(byMonth.map((c) => [`${c.year}-${c.month}`, c.count]));
  const years = [...new Set(byMonth.map((c) => c.year))];
  const all = years.length ? Array.from({ length: Math.max(...years) - Math.min(...years) + 1 }, (_, i) => Math.max(...years) - i) : [];
  return { years: all, max: Math.max(0, ...byMonth.map((c) => c.count)), count: (y, m) => cells.get(`${y}-${m}`) ?? 0 };
}

/** Cell strength, 22-100%, for a colour-mix with the accent; 0 means empty. */
export const heat = (count: number, max: number) => (count && max ? Math.round(22 + (78 * count) / max) : 0);
